import { useState } from 'react';
import { Link } from 'react-router-dom';
import Reveal from '../components/Reveal.jsx';

/**
 * The domain security checker.
 *
 * Everything it reports is public — DNS records anyone can query, headers any
 * visitor receives — and the copy says so, because a tool that implied it was
 * scanning someone's network would be both dishonest and a liability. The
 * value is the reading, not the access.
 */

const TONE = {
  pass: { label: 'Pass', className: 'f-pass' },
  warn: { label: 'Check', className: 'f-warn' },
  fail: { label: 'Fix', className: 'f-fail' },
  info: { label: 'Note', className: 'f-info' },
};

const GRADE_NOTE = {
  A: 'Genuinely well configured. Most domains do not look like this.',
  B: 'Solid. A couple of things left on the table.',
  C: 'The basics are there, with real gaps above them.',
  D: 'Several things here are worth fixing this week.',
  F: 'The fundamentals are missing. Start at the top.',
};

function Finding({ f }) {
  const tone = TONE[f.status] || TONE.info;
  return (
    <li className={`finding ${tone.className}`}>
      <div className="finding-head">
        <span className="finding-flag">{tone.label}</span>
        <h4>{f.label}</h4>
      </div>
      <p className="finding-headline">{f.headline}</p>
      {f.detail && <p className="finding-detail mono">{f.detail}</p>}
      {f.fix && <p className="finding-fix dim">{f.fix}</p>}
    </li>
  );
}

export default function Checker() {
  const [domain, setDomain] = useState('');
  const [state, setState] = useState('idle');   // idle | running | done | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  async function run(event) {
    event.preventDefault();
    if (state === 'running' || !domain.trim()) return;

    setState('running');
    setError(null);
    setResult(null);

    try {
      const res = await fetch('/api/check', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ domain }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'That check could not be completed.');
      setResult(body);
      setState('done');
    } catch (err) {
      setError(err.message);
      setState('error');
    }
  }

  const counts = result
    ? result.groups.flatMap((g) => g.findings).reduce((acc, f) => {
        acc[f.status] = (acc[f.status] || 0) + 1;
        return acc;
      }, {})
    : null;

  return (
    <>
      <section className="page-head">
        <div className="shell">
          <p className="eyebrow">Free tool</p>
          <h1>Domain Check</h1>
          <p className="lede" style={{ marginTop: '1.2rem' }}>
            The first pass we run on day one of an engagement. Can anyone send email as you?
            Is your certificate about to lapse? Does your site tell a browser to protect it?
          </p>
        </div>
      </section>

      <section className="section" style={{ paddingTop: '2.5rem' }}>
        <div className="shell">
          <form onSubmit={run} className="check-form">
            <label className="sr-only" htmlFor="domain">Domain to check</label>
            <input
              id="domain"
              name="domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="yourcompany.com"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck="false"
              inputMode="url"
            />
            <button type="submit" className="btn btn-primary" disabled={state === 'running'}>
              {state === 'running' ? 'Checking…' : 'Check'}
            </button>
          </form>

          <p className="faint" style={{ marginTop: '0.9rem', fontSize: '0.85rem', maxWidth: '46rem' }}>
            Everything below is public information — DNS records anyone can query and the headers
            any visitor receives. Nothing is scanned, probed or logged in beyond the domain name
            and its grade. This is not a penetration test.
          </p>

          {error && (
            <p className="check-error" role="alert">{error}</p>
          )}

          {state === 'running' && (
            <p className="dim mono" style={{ marginTop: '2rem' }} aria-live="polite">
              Resolving DNS, reading mail policy, opening a TLS connection…
            </p>
          )}

          {result && (
            <div aria-live="polite">
              <Reveal className="check-score">
                <div className={`grade grade-${result.grade}`}>
                  <span className="grade-letter">{result.grade}</span>
                  <span className="grade-pct mono">{result.percent}%</span>
                </div>
                <div>
                  <h2 style={{ wordBreak: 'break-word' }}>{result.host}</h2>
                  <p className="dim" style={{ marginTop: '0.6rem' }}>{GRADE_NOTE[result.grade]}</p>
                  {counts && (
                    <p className="mono faint" style={{ fontSize: '0.8rem', margin: '0.6rem 0 0' }}>
                      {counts.pass || 0} passing · {counts.warn || 0} to check · {counts.fail || 0} to fix
                    </p>
                  )}
                </div>
              </Reveal>

              {result.groups.map((group) => (
                <div key={group.title} className="check-group">
                  <h3 className="check-group-head">{group.title}</h3>
                  <ul className="findings">
                    {group.findings.map((f) => <Finding key={f.id} f={f} />)}
                  </ul>
                </div>
              ))}

              <div className="railed check-cta">
                <h3>Want the rest of the picture?</h3>
                <p className="dim" style={{ marginTop: '0.5rem' }}>
                  This looks at what the public internet can see. A real assessment covers your
                  internal controls, identity and access, endpoints, and backup posture — and
                  comes back as a prioritised roadmap rather than a scan dump.
                </p>
                <p style={{ marginTop: '1.2rem' }}>
                  <Link className="btn btn-primary" to="/contact">Talk to us about an assessment</Link>
                </p>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
